import * as Joi from 'joi';
import { CandidateProfileFormData, CandidateProfileIsDisableInputs } from '../../../models';
import {
  getEmailSchemaPair,
  getJoiObject,
  getMasterCodeObjectDropdownSchema,
  getPhoneNumberSchema,
  reEnterEmailSchemaPair,
  validateDate,
  validateFloor,
  validateHouseBlockNo,
  validatePostalCode,
  validateSingaporeCountry,
  validateStreetName,
  validateUnit,
} from '../../../utils/JoiUtils';

const countryCodeKey = 'Country Code';
const telephoneKey = 'Telephone No.';
const getNextOfKinContactNumField = (fieldName: string | undefined) =>
  fieldName ?? 'Emergency Contact No.';
const getNextOfKinNameField = (fieldName: string | undefined) =>
  fieldName ?? 'Emergency Contact Name';

const getNextOfKinNameJoiObject = (
  nextOfKinNameField: string | undefined,
  disableValidation = false
) =>
  getJoiObject(
    Joi.string()
      .required()
      .max(66)
      .required()
      .messages({
        'any.required': `${getNextOfKinNameField(nextOfKinNameField)} is not allowed to be empty`,
        'string.empty': `${getNextOfKinNameField(nextOfKinNameField)} is not allowed to be empty`,
        'string.max': `${getNextOfKinNameField(nextOfKinNameField)} is too long`,
      }),
    disableValidation
  );

const getNextOfKinContactNoJoiObject = (
  nextOfKinContactNumField: string | undefined,
  disableValidation = false
) =>
  getJoiObject(
    Joi.when('nextOfKinCountryCode', {
      is: validateSingaporeCountry,
      then: getPhoneNumberSchema(
        false,
        getNextOfKinContactNumField(nextOfKinContactNumField),
        8,
        8
      ),
      otherwise: getPhoneNumberSchema(false, getNextOfKinContactNumField(nextOfKinContactNumField)),
    }),
    disableValidation
  );

const schemaCreateCandidate = (
  isDisableInputs?: CandidateProfileIsDisableInputs,
  nextOfKinNameField?: string,
  nextOfKinContactNumField?: string
) =>
  Joi.object<CandidateProfileFormData>({
    hanyupinyinName: getJoiObject(
      Joi.string().allow('').max(66).messages({
        'string.max': 'Hanyu Pinyin Name is too long',
      }),
      isDisableInputs?.hanyupinyinName
    ),
    gender: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Gender'),
      isDisableInputs?.gender
    ),
    email: getJoiObject(getEmailSchemaPair('Email Address'), isDisableInputs?.email),
    reEnterEmail: getJoiObject(
      reEnterEmailSchemaPair('Re-enter Email Address'),
      isDisableInputs?.reEnterEmail
    ),
    race: getJoiObject(getMasterCodeObjectDropdownSchema(false, 'Race'), isDisableInputs?.race),
    nationality: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Nationality'),
      isDisableInputs?.nationality
    ),
    prStatus: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'PR status'),
      isDisableInputs?.prStatus
    ),
    nationalityIssueDate: getJoiObject(
      Joi.custom((...joiParams) => validateDate(true, ...joiParams)).messages({
        'date.pattern.notValid': 'Nationality issue date must be a valid date',
        'date.pattern.isAfter': 'Nationality issue date cannot be of future dates',
      }),
      isDisableInputs?.nationalityIssueDate
    ),
    citizenship: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Citizenship Type'),
      isDisableInputs?.citizenship
    ),
    occupation: getJoiObject(
      getMasterCodeObjectDropdownSchema(true, 'Occupation'),
      isDisableInputs?.occupation
    ),
    houseOrBlkNo: getJoiObject(
      Joi.string()
        .max(10)
        .required()
        .when('postalCode', {
          is: Joi.string().not('000000'),
          then: Joi.custom(validateHouseBlockNo),
        })
        .messages({
          'any.required': 'House/Block No. is not allowed to be empty.',
          'string.empty': 'House/Block No. is not allowed to be empty.',
          'string.max': 'House/Block No. is too long.',
          'string.pattern.houseBlockNo': 'First character must be number.',
        }),
      isDisableInputs?.houseOrBlkNo
    ),
    streetName: getJoiObject(
      Joi.string().max(32).required().custom(validateStreetName).messages({
        'any.required': 'Street Name is not allowed to be empty.',
        'string.empty': 'Street Name is not allowed to be empty.',
        'string.max': 'Street Name is too long.',
        'string.pattern.streetName': 'First character must be alphabet letter.',
      }),
      isDisableInputs?.streetName
    ),
    building: getJoiObject(
      Joi.string().max(66).allow('').messages({
        'string.max': 'Building is too long.',
      }),
      isDisableInputs?.building
    ),
    floor: getJoiObject(
      Joi.string().max(2).allow('').custom(validateFloor).messages({
        'string.max': 'Floor is too long.',
        'string.pattern.floor': 'Please enter number.',
      }),
      isDisableInputs?.floor
    ),
    unit: getJoiObject(
      Joi.string().max(5).allow('').custom(validateUnit).messages({
        'string.max': 'Unit is too long.',
        'string.pattern.unit': 'Please enter number and/or alphabet letter.',
      }),
      isDisableInputs?.unit
    ),
    postalCode: getJoiObject(
      Joi.string().max(6).required().custom(validatePostalCode).messages({
        'any.required': 'Postal Code is not allowed to be empty.',
        'string.empty': 'Postal Code is not allowed to be empty.',
        'string.max': 'Postal Code is too long.',
        'string.pattern.postalCode': 'Please enter 6 digits.',
      }),
      isDisableInputs?.postalCode
    ),
    sameAsRegisteredAddress: getJoiObject(
      Joi.any().optional(),
      isDisableInputs?.sameAsRegisteredAddress
    ),
    preferredAddress: getJoiObject(Joi.required(), isDisableInputs?.preferredAddress),
    foreignAddress1: getJoiObject(
      Joi.when('preferredAddress', {
        is: 'F',
        then: Joi.string().required().max(60).messages({
          'any.required':
            'Foreign Address 1 is not allowed to be empty when foreign address is the preferred address.',
          'string.empty':
            'Foreign Address 1 is not allowed to be empty when foreign address is the preferred address.',
          'string.max': 'Foreign Address 1 is too long.',
        }),
        otherwise: Joi.string().allow('').max(60).messages({
          'string.max': 'Foreign Address 1 is too long.',
        }),
      }),
      isDisableInputs?.foreignAddress1
    ),
    foreignAddress2: getJoiObject(
      Joi.string().allow('').max(60).messages({
        'string.max': 'Foreign Address 2 is too long.',
      }),
      isDisableInputs?.foreignAddress2
    ),
    foreignAddress3: getJoiObject(
      Joi.string().allow('').max(60).messages({
        'string.max': 'Foreign Address 3 is too long.',
      }),
      isDisableInputs?.foreignAddress3
    ),
    telephoneCountryCode: getJoiObject(
      Joi.when('preferredTelephoneOrMobile', {
        is: 'T',
        then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
        otherwise: Joi.when('telephoneContactNumber', {
          is: Joi.string().exist(),
          then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
          otherwise: getMasterCodeObjectDropdownSchema(true, countryCodeKey),
        }),
      }),
      isDisableInputs?.telephoneCountryCode
    ),
    telephoneContactNumber: getJoiObject(
      Joi.when('telephoneCountryCode', {
        is: validateSingaporeCountry,
        then: Joi.when('preferredTelephoneOrMobile', {
          is: 'T',
          then: getPhoneNumberSchema(false, telephoneKey, 8, 8),
          otherwise: getPhoneNumberSchema(true, telephoneKey, 8, 8),
        }),
        otherwise: Joi.when('preferredTelephoneOrMobile', {
          is: 'T',
          then: getPhoneNumberSchema(false, telephoneKey),
          otherwise: getPhoneNumberSchema(true, telephoneKey),
        }),
      }),
      isDisableInputs?.telephoneContactNumber
    ),
    mobileCountryCode: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, countryCodeKey),
      isDisableInputs?.mobileCountryCode
    ),
    mobileContactNumber: getJoiObject(
      Joi.when('mobileCountryCode', {
        is: validateSingaporeCountry,
        then: getPhoneNumberSchema(false, 'Mobile No.', 8, 8),
        otherwise: getPhoneNumberSchema(false, 'Mobile No.'),
      }),
      isDisableInputs?.mobileContactNumber
    ),
    preferredTelephoneOrMobile: getJoiObject(
      Joi.required(),
      isDisableInputs?.preferredTelephoneOrMobile
    ),
    nextOfKinName: getNextOfKinNameJoiObject(nextOfKinNameField, isDisableInputs?.nextOfKinName),
    nextOfKinCountryCode: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, countryCodeKey),
      isDisableInputs?.nextOfKinCountryCode
    ),
    nextOfKinContactNumber: getNextOfKinContactNoJoiObject(
      nextOfKinContactNumField,
      isDisableInputs?.nextOfKinContactNumber
    ),
    nextOfKinRelationshipToCandidate: getJoiObject(
      getMasterCodeObjectDropdownSchema(false, 'Relationship to Candidate'),
      isDisableInputs?.nextOfKinRelationshipToCandidate
    ),
  }).options({ stripUnknown: true });

export default schemaCreateCandidate;
